import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { loadStripe } from '@stripe/stripe-js';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { change, clearSubmitErrors, Field, Form, formValueSelector, reduxForm } from 'redux-form';

import { BaseForm } from 'components/Form';
import Loading from 'components/Loading';
import MessageBox from 'components/MessageBox/index';
import Retry from 'components/Retry';
import { fetchById as fetchFacility } from 'entities/Facility/actions';
import { fetchToken as fetchClientToken } from 'entities/Payment/actions';
import { formatZip } from 'util/formatters';
import { renderEnhancedField } from 'util/form/renderers';
import BraintreeController from './braintree';
import './styles.scss';

// the payment methods we can offer, in the order we offer them
const METHODS = {
  CREDIT_CARD: 'creditCard',
  PAY_PAL: 'payPal',
  APPLE_PAY: 'applePay',
  GOOGLE_PAY: 'googlePay',
};

// the Canadian provinces offered in the state list, so that we can tell a
// Canadian billing address from a US one
const CANADIAN_PROVINCES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
];

// how each method is labelled, in the selector and in the copy around it
const METHOD_LABELS = {
  [METHODS.CREDIT_CARD]: { text: 'Card', icon: ['fas', 'credit-card'] },
  [METHODS.PAY_PAL]: { text: 'PayPal', icon: ['fab', 'paypal'] },
  [METHODS.APPLE_PAY]: { text: 'Apple Pay', icon: ['fab', 'apple-pay'] },
  [METHODS.GOOGLE_PAY]: { text: 'Google Pay', icon: ['fab', 'google-pay'] },
};

// the message we show whenever we cannot take a payment at all
const UNAVAILABLE =
  'We are currently unable to accept payments. Please go back and select a postpaid rate or try again later.';

/*
 * How Braintree should style the inputs inside the Hosted Fields iframes, so
 * that they match the Bootstrap inputs around them.
 *
 * Note that Hosted Fields only supports system-installed fonts: the iframes
 * are served from Braintree, and loading a web font into them would pull an
 * external asset into the card-data frame, which SAQ A does not allow. That
 * rules out Roc Grotesk, which we serve from Typekit, so the best we can do is
 * the rest of the site's fallback stack. 'inherit' does not work either, since
 * inside the iframe it inherits from Braintree's document rather than ours.
 */
const SYSTEM_FONT_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

const HOSTED_FIELD_STYLES = {
  input: {
    'font-size': '1.25rem',
    'font-family': SYSTEM_FONT_STACK,
    'font-weight': '400',
    color: '#495057',
  },
  ':focus': {
    color: '#495057',
  },
  '.invalid': {
    color: '#dc3545',
  },
  '.valid': {
    color: '#495057',
  },
};

/* Pay form. */
class PayForm extends BaseForm {
  constructor(props) {
    // parent, for lifecycle logging
    super(props);

    // keep track of facility loading
    this.state = {
      ...this.state,
      fetchError: false,
      loading: false,
      facility: undefined,
      clientToken: null,

      // payment widget state
      stripeElements: null,
      stripePaymentElement: null,
      stripeInstance: null,
      processorError: null,
      widgetMounted: false,
      widgetKey: 0,

      // whether Stripe's payment element has everything it needs; Braintree
      // tracks this per method instead
      widgetComplete: false,

      // which method the customer is paying with
      selectedMethod: null,

      // which Braintree methods are actually usable
      availableMethods: [],

      // card entry state; cardComplete tracks whether every Hosted Field
      // holds a valid value, which is as much as we can know about them
      cardComplete: false,
      cardType: null,

      // the error from whichever method the customer last tried
      methodError: null,

      // set once a wallet has handed us a nonce
      walletMethod: null,

      // true while a wallet's button is being drawn, so the customer sees
      // that something is happening rather than an empty panel
      renderingWallet: false,
    };

    // the Braintree SDK components; this is not render state, so it is not
    // held on the component state
    this.braintree = null;

    // the wallets whose buttons we have already rendered
    this.renderedWallets = new Set();

    // set when we clear the nonce ourselves, to switch methods
    this.clearingNonceOnMethodChange = false;
  }

  componentDidMount() {
    // parent, for lifecycle logging
    super.componentDidMount();

    if (!this.state.loading) {
      this.loadFacility(this.props);
    }
  }

  // bind address auto-completion for the billing address
  addressAutoComplete = () => ({
    keyField: 'customer.address.street',
    addressFields: {
      street: 'customer.address.street',
      street2: 'customer.address.street2',
      city: 'customer.address.city',
      state: 'customer.address.state',
      zip: 'customer.address.zip',
    },
  });

  loadFacility(props) {
    if (props.facilityId) {
      // if we already have the desired facility, do nothing
      if (this.state.facility && String(this.state.facility.id) === String(props.facilityId)) {
        return;
      }

      // loading...
      this.setState({
        fetchError: false,
        loading: true,
        facility: null,
      });

      // fetch it
      props
        .fetchById(props.facilityId)
        .then((facility) => {
          // set it on the state
          this.setState({
            fetchError: false,
            facility: facility,
          });

          // initialize payments once facility is loaded
          this.initializePayments(facility);
        })
        .catch((e) => {
          // log it
          console.error('Error fetching facility:', e);

          // flag it so we can show an error
          this.setState({ fetchError: true });
        })
        .finally(() => {
          // no longer loading
          this.setState({
            loading: false,
          });
        });
    } else if (this.state.facility) {
      // clear the facility
      this.setState({
        fetchError: false,
        loading: false,
        facility: undefined,
      });
    }
  }

  initializePayments(facility) {
    // we require HTTPS
    const isSecure = window.location.protocol === 'https:';
    if (!isSecure) {
      console.error('Payment widgets require a secure connection (HTTPS).');
      this.setState({
        processorError: 'A secure connection (HTTPS) is required to process payments.',
      });
      return;
    }

    // get the configured processor
    const digitalProcessor = facility?.digitalPaymentProcessor;
    let isBraintree = digitalProcessor === 'BTD';
    let isStripe = digitalProcessor === 'STD';

    // we need the provider's token
    this.props.loadClientToken(this.props.facilityId).then((token) => {
      if (!token) {
        console.error('Error fetching payment token');
        this.setState({ processorError: UNAVAILABLE });
        return;
      }
      this.setState({ clientToken: token });

      // stand up the correct provider
      if (isBraintree) {
        this.initializeBraintree(facility, token);
      } else if (isStripe) {
        this.initializeStripe(token);
      }
    });
  }

  /**
   * Stands up the Braintree SDK components that replace the deprecated
   * drop-in. Each method is offered only if the hub reports it as up, and
   * only if the browser and the facility can actually support it.
   */
  async initializeBraintree(facility, token) {
    const controller = new BraintreeController(token);
    this.braintree = controller;

    try {
      // everything else is built on the client
      await controller.createClient();

      // start fraud-data collection (PNG-552)
      await controller.createDataCollector();
    } catch (e) {
      console.error('Error creating Braintree client', e);
      this.setState({ processorError: UNAVAILABLE });
      return;
    }

    // if we were torn down while the client was being created, stop here
    if (this.braintree !== controller) {
      return;
    }

    // work out which methods the hub has enabled for the web client
    const enabled = this.props.status?.payments || {};
    const available = [];

    // cards, via Hosted Fields
    if (enabled.creditCard) {
      try {
        await controller.createHostedFields({
          selectors: {
            cardholderName: `#braintree-card-name-${this.state.widgetKey}`,
            number: `#braintree-card-number-${this.state.widgetKey}`,
            expirationDate: `#braintree-card-expiration-${this.state.widgetKey}`,
            cvv: `#braintree-card-cvv-${this.state.widgetKey}`,
          },
          styles: HOSTED_FIELD_STYLES,
          onValidityChange: (allValid) => this.setState({ cardComplete: allValid }),
          onCardTypeChange: (card) => this.setState({ cardType: card }),
        });
        available.push(METHODS.CREDIT_CARD);
      } catch (e) {
        console.error('Error creating Braintree Hosted Fields', e);
      }
    }

    // PayPal
    if (enabled.payPal) {
      try {
        if (await controller.setupPayPal()) {
          available.push(METHODS.PAY_PAL);
        }
      } catch (e) {
        console.error('Error setting up PayPal', e);
      }
    }

    // Apple Pay, which needs a supporting browser and device
    if (enabled.applePay && BraintreeController.applePaySupported()) {
      try {
        await controller.setupApplePay();
        available.push(METHODS.APPLE_PAY);
      } catch (e) {
        console.error('Error setting up Apple Pay', e);
      }
    }

    // Google Pay, which needs a Google-issued merchant ID in production but
    // can run without one against the sandbox
    if (enabled.googlePay) {
      const googlePayMerchantId = facility?.braintree?.googlePayMerchantId;
      if (controller.environment() === 'production' && !googlePayMerchantId) {
        console.error('Google Pay is enabled but the facility has no Google Pay merchant ID');
      } else {
        try {
          const ready = await controller.setupGooglePay(googlePayMerchantId);
          if (ready) {
            available.push(METHODS.GOOGLE_PAY);
          }
        } catch (e) {
          console.error('Error setting up Google Pay', e);
        }
      }
    }

    // if we were torn down while setting up, stop here
    if (this.braintree !== controller) {
      return;
    }

    // if nothing is usable, we cannot take a payment
    if (!available.length) {
      this.setState({ processorError: UNAVAILABLE });
      return;
    }

    // componentDidUpdate renders the button for whichever method we land on;
    // note that BaseForm's setState takes a 'force' flag in place of React's
    // callback and drops the callback entirely, so there is no completion
    // callback to hang that off here
    this.setState({
      availableMethods: available,
      selectedMethod: available[0],
      widgetMounted: true,
    });
  }

  /**
   * Renders a wallet's button, once, the first time its method is selected.
   * PayPal and Google Pay both need a visible container to render into, so we
   * cannot do this while the method is hidden.
   */
  renderWalletButton(method) {
    // Apple Pay's button is ours, so React renders it
    if (!this.braintree || method === METHODS.CREDIT_CARD || method === METHODS.APPLE_PAY) {
      return;
    }

    // only render each button once
    if (this.renderedWallets.has(method)) {
      return;
    }
    this.renderedWallets.add(method);

    if (method === METHODS.PAY_PAL) {
      this.setState({ renderingWallet: true });
      this.braintree
        .renderPayPalButton({
          container: `#braintree-paypal-button-${this.state.widgetKey}`,
          getAmount: () => String(this.props.amount),
          onNonce: this.handleWalletNonce,
          onError: (e) => this.handleWalletError(METHODS.PAY_PAL, e),
        })
        .then(() => this.setState({ renderingWallet: false }))
        .catch((e) => {
          this.setState({ renderingWallet: false });
          this.handleWalletRenderError(method, e);
        });
    } else if (method === METHODS.GOOGLE_PAY) {
      try {
        this.braintree.renderGooglePayButton({
          container: `#braintree-googlepay-button-${this.state.widgetKey}`,
          onClick: this.handleGooglePayClick,
        });
      } catch (e) {
        this.handleWalletRenderError(method, e);
      }
    }
  }

  /**
   * Reports a wallet button that would not render. Without this the customer
   * is left looking at an empty panel with no way to pay and nothing to tell
   * them why, so say so and let them fall back to another method.
   */
  handleWalletRenderError(method, e) {
    console.error(`Error rendering the ${method} button`, e);

    // let it be retried if the customer switches away and back
    this.renderedWallets.delete(method);

    // drop the method, so the selector stops offering something that cannot work
    this.setState((prevState) => {
      const remaining = prevState.availableMethods.filter((m) => m !== method);
      return {
        availableMethods: remaining,
        selectedMethod:
          prevState.selectedMethod === method ? remaining[0] : prevState.selectedMethod,
        methodError: remaining.length
          ? 'That payment method is unavailable right now. Please choose another.'
          : null,
        processorError: remaining.length ? prevState.processorError : UNAVAILABLE,
      };
    });
  }

  /* Stands up the Stripe payment element. */
  initializeStripe(token) {
    loadStripe(token)
      .then((stripe) => {
        // did it fail?
        if (!stripe) {
          this.setState({ processorError: UNAVAILABLE });
          return;
        }

        // create elements
        const elements = stripe.elements({
          mode: 'payment',
          amount: Math.max(Math.round(this.props.amount * 100), 50),
          currency: 'usd',
          paymentMethodCreation: 'manual',
          appearance: { theme: 'flat' },
        });
        const paymentElement = elements.create('payment');

        // wire up events
        paymentElement.on('change', (event) => {
          this.setState({ widgetComplete: event.complete });
        });

        // mount
        paymentElement.mount(`#stripe-payment-element-${this.state.widgetKey}`);

        // set the instance and flag as mounted
        this.setState({
          stripeInstance: stripe,
          stripeElements: elements,
          stripePaymentElement: paymentElement,
          widgetMounted: true,
        });
      })
      .catch((err) => {
        console.error('Stripe load error', err);
        this.setState({ processorError: UNAVAILABLE });
      });
  }

  /* Captures the nonce a wallet handed us, ready for the customer to submit. */
  handleWalletNonce = (nonce, method) => {
    // store it on the form so that submission picks it up
    this.props.dispatch(change(this.props.form, 'payment.nonce', nonce));

    // and collect the device data to go with it
    this.braintree?.getDeviceData().then((deviceData) => {
      this.props.dispatch(change(this.props.form, 'payment.deviceData', deviceData));
    });

    this.setState({ walletMethod: method, methodError: null });
  };

  /* Reports a wallet failure without losing the rest of the form. */
  handleWalletError = (method, e) => {
    console.error(`Error taking payment via ${method}`, e);

    // only point them at a card if we are actually offering one
    const canUseCard = this.state.availableMethods.includes(METHODS.CREDIT_CARD);
    this.setState({
      methodError: canUseCard
        ? 'We were unable to complete that payment. Please try again or pay by card.'
        : 'We were unable to complete that payment. Please try again.',
    });
  };

  /* Opens the Apple Pay sheet; must run inside the click handler. */
  handleApplePayClick = () => {
    this.setState({ methodError: null });
    this.braintree?.startApplePay({
      amount: this.props.amount,
      displayName: this.state.facility?.braintree?.brandName || `Park 'N Go`,
      onNonce: this.handleWalletNonce,
      onError: (e) => this.handleWalletError(METHODS.APPLE_PAY, e),
    });
  };

  /* Opens the Google Pay sheet. */
  handleGooglePayClick = () => {
    this.setState({ methodError: null });
    this.braintree?.startGooglePay({
      amount: this.props.amount,
      onNonce: this.handleWalletNonce,
      onError: (e) => this.handleWalletError(METHODS.GOOGLE_PAY, e),
    });
  };

  /* Switches the selected payment method, discarding any captured nonce. */
  selectMethod = (method) => {
    if (method === this.state.selectedMethod) {
      return;
    }

    // a nonce only belongs to the method that produced it
    if (this.props.nonce) {
      // clearing the nonce normally means a payment failed and we need a fresh
      // token, so flag that this one is ours and must not reload the widgets
      this.clearingNonceOnMethodChange = true;
      this.props.dispatch(change(this.props.form, 'payment.nonce', null));
      this.props.dispatch(change(this.props.form, 'payment.deviceData', null));
    }

    // componentDidUpdate renders the newly selected method's button; it cannot
    // be done from a setState callback, because BaseForm's setState takes a
    // 'force' flag in that position and never calls back
    this.setState({ selectedMethod: method, walletMethod: null, methodError: null });
  };

  componentWillUnmount() {
    super.componentWillUnmount();

    // tear down the payment processor instances
    if (this.braintree) {
      this.braintree.teardown();
      this.braintree = null;
    }
    if (this.state.stripePaymentElement) {
      this.state.stripePaymentElement.destroy();
    }
  }

  reloadWidget() {
    // tear down the payment processor instances
    if (this.braintree) {
      this.braintree.teardown();
      this.braintree = null;
    }
    if (this.state.stripePaymentElement) {
      try {
        this.state.stripePaymentElement.destroy();
      } catch (e) {
        console.error('Error destroying Stripe element', e);
      }
    }

    // the reload gives every container a new key, so the buttons we rendered
    // into the old ones are gone
    this.renderedWallets.clear();

    // reset the state
    this.setState((prevState) => ({
      widgetKey: (prevState.widgetKey || 0) + 1,
      stripeElements: null,
      stripePaymentElement: null,
      stripeInstance: null,
      processorError: null,
      widgetMounted: false,
      widgetComplete: false,
      clientToken: null,
      selectedMethod: null,
      availableMethods: [],
      cardComplete: false,
      cardType: null,
      methodError: null,
      walletMethod: null,
      renderingWallet: false,
    }));
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    // load facility if not already loaded
    if (!this.state.loading && this.state.facility === undefined) {
      this.loadFacility(this.props);
    }

    // update stripe amount if changed
    if (this.state.stripeElements && prevProps.amount !== this.props.amount) {
      this.state.stripeElements.update({
        amount: Math.max(Math.round(this.props.amount * 100), 50),
      });
    }

    /*
     * Render the selected wallet's button once the method is chosen and its
     * container is on screen. This has to happen here rather than from a
     * setState callback: BaseForm overrides setState with a 'force' flag in
     * the callback's position and never calls back, so anything passed there
     * is silently dropped.
     */
    if (prevState.selectedMethod !== this.state.selectedMethod && this.state.selectedMethod) {
      this.renderWalletButton(this.state.selectedMethod);
    }

    // if the nonce was cleared in a manner other than a full-form reset,
    // we need to re-render the payment widget; clearing it ourselves to switch
    // methods does not count, since those widgets are still perfectly good
    if (prevProps.nonce && !this.props.nonce && !this.props.pristine) {
      if (this.clearingNonceOnMethodChange) {
        this.clearingNonceOnMethodChange = false;
      } else {
        this.reloadWidget();
      }
    }

    // if widgetKey has changed, we need to re-init the widgets
    if (prevState.widgetKey !== this.state.widgetKey && this.state.facility) {
      this.initializePayments(this.state.facility);
    }
  }

  /* Builds the billing address we tokenize alongside the card, for AVS. */
  billingAddressFor(values) {
    const address = values?.customer?.address;
    if (!address) {
      return null;
    }

    // the state list covers Canadian provinces as well as US states, so the
    // country has to follow whichever one was picked
    const billingAddress = {
      streetAddress: address.street,
      extendedAddress: address.street2,
      locality: address.city,
      region: address.state,
      postalCode: address.zip,
      countryCodeAlpha2: CANADIAN_PROVINCES.includes(address.state) ? 'CA' : 'US',
    };

    // Braintree rejects empty values, so send only what we actually have
    Object.keys(billingAddress).forEach((key) => {
      if (!billingAddress[key]) {
        delete billingAddress[key];
      }
    });

    return billingAddress;
  }

  handlePaymentSubmit = async (values) => {
    // if we have a nonce, submit it
    if (this.props.nonce) {
      this.props.onSubmit(values);
      return;
    }

    // process payment
    if (this.braintree && this.state.selectedMethod === METHODS.CREDIT_CARD) {
      try {
        // tokenize the card, along with the billing address so that the
        // gateway can verify it
        const nonce = await this.braintree.tokenizeCard(this.billingAddressFor(values));

        // collect the device data that goes with it
        const deviceData = await this.braintree.getDeviceData();

        // capture both on the form, and submit
        this.props.dispatch(change(this.props.form, 'payment.nonce', nonce));
        this.props.dispatch(change(this.props.form, 'payment.deviceData', deviceData));
        this.props.onSubmit({
          ...values,
          payment: { ...values?.payment, nonce: nonce, deviceData: deviceData },
        });
      } catch (err) {
        console.error('Braintree error on submission', err);
        this.setState({
          methodError:
            err?.code === 'HOSTED_FIELDS_FIELDS_INVALID'
              ? 'Please check your card details and try again.'
              : 'We were unable to process that card. Please check your details or try another card.',
        });
      }
    } else if (this.state.stripeInstance && this.state.stripeElements) {
      // first, validate the payment element
      const { error: submitError } = await this.state.stripeElements.submit();
      if (submitError) {
        console.error('Stripe validation error', submitError);
        return;
      }

      // create payment method
      const { error, paymentMethod } = await this.state.stripeInstance.createPaymentMethod({
        elements: this.state.stripeElements,
      });
      if (error) {
        console.error('Stripe error on submission', error);
        return;
      }

      // capture the payment nonce and submit
      this.props.dispatch(change(this.props.form, 'payment.nonce', paymentMethod.id));
      this.props.onSubmit({ ...values, payment: { ...values?.payment, nonce: paymentMethod.id } });
    } else {
      // shouldn't happen
      this.props.onSubmit(values);
    }
  };

  /* Renders the selector that lets the customer pick a payment method. */
  renderMethodSelector() {
    // with only one method there is nothing to choose
    if (this.state.availableMethods.length < 2) {
      return <></>;
    }

    return (
      <div className="btn-group btn-group-toggle d-flex png-payment-method-selector mb-3">
        {this.state.availableMethods.map((method) => (
          <button
            key={method}
            type="button"
            className={`btn btn-outline-primary ${
              this.state.selectedMethod === method ? 'active' : ''
            }`}
            onClick={() => this.selectMethod(method)}
            disabled={this.props.submitting}
          >
            <FontAwesomeIcon icon={METHOD_LABELS[method].icon} className="mr-2" />
            {METHOD_LABELS[method].text}
          </button>
        ))}
      </div>
    );
  }

  /* Renders the card entry fields and the billing address. */
  renderCardForm(visible) {
    const cardRequired = this.state.selectedMethod === METHODS.CREDIT_CARD;

    return (
      <div className={visible ? '' : 'd-none'}>
        {/* card details */}
        <div className="form-row">
          <div className="form-group col-12">
            <label className="col-form-label col-form-label-lg">Name on Card</label>
            <div
              id={`braintree-card-name-${this.state.widgetKey}`}
              className="form-control form-control-lg png-hosted-field"
            ></div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-12">
            <label className="col-form-label col-form-label-lg">
              Card Number
              {this.state.cardType && (
                <span className="ml-2 png-hosted-field-card-type">
                  {this.state.cardType.niceType}
                </span>
              )}
            </label>
            <div
              id={`braintree-card-number-${this.state.widgetKey}`}
              className="form-control form-control-lg png-hosted-field"
            ></div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-sm-6">
            <label className="col-form-label col-form-label-lg">Expiration</label>
            <div
              id={`braintree-card-expiration-${this.state.widgetKey}`}
              className="form-control form-control-lg png-hosted-field"
            ></div>
          </div>
          <div className="form-group col-sm-6">
            <label className="col-form-label col-form-label-lg">CVV</label>
            <div
              id={`braintree-card-cvv-${this.state.widgetKey}`}
              className="form-control form-control-lg png-hosted-field"
            ></div>
          </div>
        </div>

        {/* billing address, which the gateway uses to verify the card */}
        <div className="form-row mt-3 mb-0">
          <div className="form-group col mb-0">
            <h3>Billing Address</h3>
          </div>
        </div>

        {/* address lines */}
        <div className="form-row">
          {/* address line 1 */}
          <div className="form-group col-md-6 has-error">
            <Field
              type="text"
              label="Address Line 1"
              name="customer.address.street"
              autoComplete="address-line1"
              labelClassName="col-form-label col-form-label-lg"
              className="form-control form-control-lg"
              component={renderEnhancedField}
              placeholder="Address Line 1"
              tooltip={`The billing address for this card`}
              maxLength="255"
              required={cardRequired}
              disabled={this.props.submitting}
            />
          </div>

          {/* address line 2 */}
          <div className="form-group col-md-6 has-error">
            <Field
              type="text"
              label="Address Line 2"
              name="customer.address.street2"
              autoComplete="address-line2"
              labelClassName="col-form-label col-form-label-lg"
              className="form-control form-control-lg"
              component={renderEnhancedField}
              placeholder="Address Line 2 (Optional)"
              tooltip={`Any supplementary address information, like a suite or apartment number`}
              maxLength="64"
              required={false}
              disabled={this.props.submitting}
            />
          </div>
        </div>

        {/* city/state/zip */}
        <div className="form-row">
          {/* city */}
          <div className="form-group col-sm-4 has-error">
            <Field
              type="text"
              label="City"
              name="customer.address.city"
              autoComplete="address-level2"
              labelClassName="col-form-label col-form-label-lg"
              className="form-control form-control-lg"
              component={renderEnhancedField}
              placeholder="City"
              tooltip={`The city for this card's billing address`}
              maxLength="64"
              required={cardRequired}
              disabled={this.props.submitting}
            />
          </div>

          {/* state */}
          <div className="form-group col-sm-5 has-error">
            <Field
              type="select"
              label="State/Province"
              name="customer.address.state"
              autoComplete="address-level1"
              labelClassName="col-form-label col-form-label-lg"
              className="form-control form-control-lg"
              component={renderEnhancedField}
              placeholder="State/Province"
              tooltip={`The state or province for this card's billing address`}
              required={cardRequired}
              disabled={this.props.submitting}
            >
              {this.renderStateOptions()}
            </Field>
          </div>

          {/* zip */}
          <div className="form-group col-sm-3 has-error">
            <Field
              type="text"
              label="Postal Code"
              name="customer.address.zip"
              autoComplete="postal-code"
              labelClassName="col-form-label col-form-label-lg"
              className="form-control form-control-lg"
              component={renderEnhancedField}
              minLength="5"
              maxLength="10"
              pattern="^(\d{5})$|^(\d{5}-\d{4})$|^([A-Z]\d{1}[A-Z]-\d{1}[A-Z]\d{1})$"
              placeholder="Postal Code"
              tooltip={`The postal code for this card's billing address`}
              format={formatZip}
              required={cardRequired}
              disabled={this.props.submitting}
            />
          </div>
        </div>
      </div>
    );
  }

  /* Renders the wallet buttons. */
  renderWallets() {
    const { selectedMethod, availableMethods, walletMethod } = this.state;

    // PayPal and Google Pay render their buttons into containers of ours, so
    // those containers have to exist before we set either one up. We key that
    // off the hub's flags, which we know up front, rather than off the methods
    // we have finished setting up.
    const enabled = this.props.status?.payments || {};

    // a wallet is in play whenever the customer has picked something other
    // than the card
    const walletSelected = Boolean(selectedMethod) && selectedMethod !== METHODS.CREDIT_CARD;
    const action = this.props.original ? 'Update' : 'Book';

    return (
      <div className={walletSelected ? 'png-wallet-area' : ''}>
        {/*
          Set expectations before they leave for the wallet: approving there
          authorizes the payment but does not take it, which is not obvious.
        */}
        {walletSelected && !walletMethod && (
          <MessageBox flavor="info">
            Approve this payment with {METHOD_LABELS[selectedMethod].text}, then choose {action} to
            confirm your reservation. You will not be charged until you do.
          </MessageBox>
        )}

        {/* PayPal renders its own button into this container */}
        {enabled.payPal && (
          <div
            id={`braintree-paypal-button-${this.state.widgetKey}`}
            className={
              availableMethods.includes(METHODS.PAY_PAL) && selectedMethod === METHODS.PAY_PAL
                ? 'png-wallet-button png-paypal-button'
                : 'd-none'
            }
          ></div>
        )}

        {/* and so does Google Pay */}
        {enabled.googlePay && (
          <div
            id={`braintree-googlepay-button-${this.state.widgetKey}`}
            className={
              availableMethods.includes(METHODS.GOOGLE_PAY) && selectedMethod === METHODS.GOOGLE_PAY
                ? 'png-wallet-button png-googlepay-button'
                : 'd-none'
            }
          ></div>
        )}

        {/* Apple Pay's button is ours to render, per Apple's stylesheet */}
        {availableMethods.includes(METHODS.APPLE_PAY) && selectedMethod === METHODS.APPLE_PAY && (
          <button
            type="button"
            className="apple-pay-button apple-pay-button-white-with-line png-wallet-button"
            onClick={this.handleApplePayClick}
            disabled={this.props.submitting}
            aria-label="Pay with Apple Pay"
          ></button>
        )}

        {/* the wallet's button is on its way */}
        {this.state.renderingWallet && (
          <div className="text-center py-3">
            <Loading />
          </div>
        )}

        {/* confirm that the wallet gave us something to charge */}
        {walletMethod && walletMethod === selectedMethod && (
          <div className="mt-3">
            <MessageBox flavor="success">
              Your {METHOD_LABELS[walletMethod].text} payment is approved. Choose {action} to
              confirm your reservation.
            </MessageBox>
          </div>
        )}
      </div>
    );
  }

  render() {
    super.render();

    // do we have an error?
    if (this.state.fetchError) {
      return <Retry onRefresh={() => this.loadFacility(this.props)} />;
    } else if (!this.state.facility && this.state.loading) {
      // still loading...
      return (
        <div className="row">
          <div className="col-12 text-center png-content-loading">
            <Loading />
          </div>
        </div>
      );
    }

    // determine payment processor
    const digitalProcessor = this.state.facility?.digitalPaymentProcessor;
    let isBraintree = digitalProcessor === 'BTD';
    let isStripe = digitalProcessor === 'STD';

    // the customer can submit once the selected method has what it needs
    const readyToSubmit = isBraintree
      ? this.state.selectedMethod === METHODS.CREDIT_CARD
        ? this.state.cardComplete
        : Boolean(this.props.nonce)
      : this.state.widgetComplete;

    return (
      <Form
        id={this.props.form}
        onSubmit={this.props.handleSubmit(this.handlePaymentSubmit)}
        className="png-reservation-book-form"
      >
        {/* do we have a top-level error? */}
        {this.props.error && (
          <div className="has-error">
            <div className="png-form-error">{this.props.error}</div>
          </div>
        )}

        {/* do we have a processor error? */}
        {this.state.processorError ? (
          <MessageBox flavor="danger">{this.state.processorError}</MessageBox>
        ) : (
          <>
            {/* render the payment widgets */}
            <div className="card">
              <div className="card-header png-reservation-book-payment-type">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Pay ${this.props.amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="card-body">
                {/*
                  Braintree, via the Braintree SDK. The containers that the SDK
                  mounts into have to be in the DOM, and visible, before we set
                  it up: Hosted Fields does not mount reliably into a hidden
                  container. So they are rendered as soon as we know the
                  facility uses Braintree, and the card fields fill in as the
                  iframes load rather than being gated behind a spinner.
                */}
                {isBraintree && (
                  <div>
                    {this.renderMethodSelector()}
                    {Boolean(this.props.status?.payments?.creditCard) &&
                      this.renderCardForm(
                        !this.state.selectedMethod ||
                          this.state.selectedMethod === METHODS.CREDIT_CARD,
                      )}
                    {this.renderWallets()}

                    {/* did the selected method fail? */}
                    {this.state.methodError && (
                      <div className="mt-3">
                        <MessageBox flavor="danger">{this.state.methodError}</MessageBox>
                      </div>
                    )}
                  </div>
                )}

                {/* Stripe */}
                <div
                  key={`st-${this.state.widgetKey}`}
                  id={`stripe-payment-element-${this.state.widgetKey}`}
                  className={isStripe ? '' : 'd-none'}
                ></div>

                {/*
                  Braintree renders its own fields as they become ready, so it
                  needs no spinner of its own.
                */}
                {!this.state.widgetMounted && !isBraintree && (
                  <div className="text-center p-3">
                    <Loading />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* if this is an update to a prepaid with an amount change, show a message */}
        {this.props.original &&
        this.props.original.prepaid &&
        this.props.original.prepaid !== this.props.amount ? (
          <div className="row">
            <div className="col mt-3">
              <MessageBox flavor="info">
                You will be refunded ${this.props.original.prepaid.toFixed(2)} for the original
                reservation before being charged ${this.props.amount.toFixed(2)} for the new
                reservation.
              </MessageBox>
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className="form-row png-reservation-buttons mt-4">
          <div className="form-group png-reservation-button col-5">
            <button
              type="button"
              onClick={() => this.props.onPrevious()}
              className="btn btn-primary btn-lg png-reservation-book-previous"
            >
              Back
            </button>
          </div>

          <div className="form-group col-2"></div>

          <div className="form-group png-reservation-button col-5">
            <button
              type="submit"
              className="btn btn-primary btn-lg png-reservation-book-next"
              disabled={
                this.props.submitting ||
                this.state.processorError ||
                !this.state.widgetMounted ||
                !readyToSubmit
              }
            >
              {this.props.original ? 'Update' : 'Book'}
            </button>
          </div>
        </div>
      </Form>
    );
  }
}

// decorate with reduxForm()
PayForm = reduxForm({
  // preserve form data throughout the flow
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,

  // clear form-level errors on change
  onChange: (_, dispatch, props) => {
    if (props.error) {
      dispatch(clearSubmitErrors(props.form));
    }
  },
})(PayForm);

// map state to properties relevant to this component
const mapStateToProps = (state, ownProps) => ({
  // status
  status: state.context.status,

  // amount owed
  amount: formValueSelector(ownProps.form)(state, 'payment.amount')
    ? formValueSelector(ownProps.form)(state, 'payment.amount')
    : 0,

  // digital nonce
  nonce: formValueSelector(ownProps.form)(state, 'payment.nonce'),

  // test mode?
  testMode: state.testMode.enabled || localStorage.getItem('testMode'),
});

// map dispatch function to callback props so that the component can invoke them
const mapDispatchToProps = (dispatch, ownProps) => ({
  // fetch a single facility
  fetchById: (facilityId, test) => {
    return dispatch(fetchFacility(facilityId, test));
  },

  // loads the client payment token
  loadClientToken: (facilityId) => {
    return dispatch(fetchClientToken(facilityId)).catch((e) => {
      console.error('Error loading client payment token', e);
      return null;
    });
  },
});

// turn this into a container component
PayForm = withRouter(connect(mapStateToProps, mapDispatchToProps)(PayForm));

// set default props
PayForm.defaultProps = {
  form: 'reservationForm',
};

export default PayForm;
